import { Size } from "@/shared/sync/mediaRepository/size";
import { ColorMap, FabricCanvas } from "../../common/interfaces";
import CanvasImageRenderer from "../../utils/canvasImageRenderer";
import { loadImageFromURL } from "../../utils/image-loader";
import { MediaImageRepository } from "./media_image_repository";
import { fabric } from 'fabric'
import { MediaImageType } from "@/shared/sync/mediaRepository/media_image_type";
import { Rectangle } from "@/shared/sync/mediaRepository/rectangle";
import { FileSystemRecord } from "@/shared/sync/mediaRepository/FileSystemRecord";

export class MediaImageFallbackHandler {
    private canvas: FabricCanvas | null
    private sizeProvider: () => (Size);
    private filtersProvider: () => (ColorMap[]);
    private objects: any[];

    private constructor() { }

    private static instance: MediaImageFallbackHandler

    public static getInstance(): MediaImageFallbackHandler {
      if (!MediaImageFallbackHandler.instance) {
        MediaImageFallbackHandler.instance = new MediaImageFallbackHandler()
      }
      return MediaImageFallbackHandler.instance
    }
  
    setup(canvas: FabricCanvas, sizeProvider: () => (Size), filtersProvider: () => (ColorMap[])) {
        this.canvas = canvas;
        this.sizeProvider = sizeProvider;
        this.filtersProvider = filtersProvider;
        this.objects = this.canvas.getObjects();
    }

    async fallback(repository: MediaImageRepository, layerId: string, layerStateId: string, imgType: MediaImageType): Promise<FileSystemRecord | null> {
        switch (imgType) {
            case MediaImageType.original: {
                let retImage = await this.fallbackOriginal(repository, layerId, layerStateId);
                return retImage;
            }
            case MediaImageType.fullRes: {
                let retImage = await this.fallbackFullRes(repository, layerId, layerStateId);
                return retImage;
            }
            case MediaImageType.fitted: {
                let retImage = await this.fallbackFitted(repository, layerId, layerStateId);
                return retImage;
            }
            case MediaImageType.mask: {
                let retImage = await this.fallbackMask(repository, layerId, layerStateId);
                return retImage;
            }
            case MediaImageType.fittedMask: {
                let retImage = await this.fallbackFittedMask(repository, layerId, layerStateId);
                return retImage;
            }
            case MediaImageType.latest: {
                let retImage = await this.fallbackLatest(repository, layerId, layerStateId);
                return retImage;
            }
            return null;
          }
    }

    private getLayerForId(id: string): any {
        // @ts-ignore
        let layer = this.objects.find(o => o.id === id || o.bazaartGuid === id)
        if (!layer) {
            this.objects = this.canvas.getObjects();

            layer = this.objects.find(o => o.id === id || o.bazaartGuid === id);
        }
        return layer;
    }

    private async fallbackOriginal(repository: MediaImageRepository, layerId: string, layerStateId: string): Promise<FileSystemRecord | null>{
        return Promise.resolve(null);
    }

    private async fallbackFullRes(repository: MediaImageRepository, layerId: string, layerStateId: string): Promise<FileSystemRecord | null>{
        return repository.getRecord(layerId, layerStateId, MediaImageType.original)
    }

    private async fallbackFitted(repository: MediaImageRepository, layerId: string, layerStateId: string): Promise<FileSystemRecord | null>{
        let fittedRecord = await this.crop(repository, layerId, layerStateId, MediaImageType.fullRes, MediaImageType.fitted);
        return fittedRecord;
    }

    private async fallbackMask(repository: MediaImageRepository, layerId: string, layerStateId: string): Promise<FileSystemRecord | null>{
        // running this check first to prevent an endless recursion
        let hasFittedMask = await repository.hasRecord(layerId, layerStateId, MediaImageType.fittedMask);
        let fullRes = await repository.getRecord(layerId, layerStateId, MediaImageType.fullRes);
        if (!hasFittedMask || !(await fullRes?.getBlobUrl())){
            return null;
        }
        let fittedMask = await repository.getRecord(layerId, layerStateId, MediaImageType.fittedMask);
        let fittedMaskImage = await repository._mediaImageRepositoryProcessing.loadImage(await  fittedMask.getBlobUrl());
        let fullResImage = await repository._mediaImageRepositoryProcessing.loadImage(await fullRes.getBlobUrl());
        
        let layer = this.getLayerForId(layerId);
        
        let maskSize = new Size(fullResImage.width, fullResImage.height);
        let bb = new Rectangle(layer.boundingBox.x, layer.boundingBox.y, layer.boundingBox.width, layer.boundingBox.height);
        let roi = bb.multiply(maskSize);
        
        let paddedMask = await repository._mediaImageRepositoryProcessing.padImage(fittedMaskImage, maskSize, roi, '#ffffff');
        let grayscalePaddedMask = await repository._mediaImageRepositoryProcessing.extractGreyMaskFromColorImage(paddedMask.src)

        await repository.storeImageBase64(layerId, layerStateId, MediaImageType.mask, grayscalePaddedMask.src)
        
        let maskRecord = await repository.getRecord(layerId, layerStateId, MediaImageType.mask);
        return maskRecord;
    }

    private async fallbackFittedMask(repository: MediaImageRepository, layerId: string, layerStateId: string): Promise<FileSystemRecord | null>{
        let fittedMaskRecord = await this.crop(repository, layerId, layerStateId, MediaImageType.mask, MediaImageType.fittedMask);
        return fittedMaskRecord;
    }

    private async fallbackLatest(repository: MediaImageRepository, layerId: string, layerStateId: string): Promise<FileSystemRecord | null>{
        let fittedMaskRecord = await repository.getRecord(layerId, layerStateId, MediaImageType.fittedMask);
        let fittedImageRecord = await repository.getRecord(layerId, layerStateId, MediaImageType.fitted);
        if (!(await fittedMaskRecord?.getBlobUrl()) || !(await fittedImageRecord?.getBlobUrl())) {
            return null;
        }
        let fittedMask = await repository._mediaImageRepositoryProcessing.loadImage(await fittedMaskRecord.getBlobUrl());
        let fittedImage = await repository._mediaImageRepositoryProcessing.loadImage(await fittedImageRecord.getBlobUrl());
        
        let layer = this.getLayerForId(layerId);
        let canvasSize = this.sizeProvider();
        let filters = this.filtersProvider();

        let maskedImageSrc = await repository._mediaImageRepositoryProcessing.applyMaskToImage(fittedMask, fittedImage);
        const maskedImage: any = await loadImageFromURL(maskedImageSrc)
        
        const element = new fabric.StaticImage(maskedImage, {})
        Object.assign(element, layer);
        let renderedElement = await CanvasImageRenderer.getInstance().render(element, canvasSize, filters)
        let base64LatestImage = renderedElement._element.src;

        await repository.storeImageBase64(layerId, layerStateId, MediaImageType.latest, base64LatestImage);
        let latestRecord = repository.getRecord(layerId, layerStateId, MediaImageType.latest);
        return latestRecord;
    }
   
    private async crop(repository: MediaImageRepository, layerId: string, layerStateId: string, srcMediaImageType: MediaImageType, targetMediaImageType: MediaImageType){
        let srcRecord = await repository.getRecord(layerId, layerStateId, srcMediaImageType);
        if (!(await srcRecord?.getBlobUrl())){
            return null;
        }
        let srcImage = await repository._mediaImageRepositoryProcessing.loadImage(await srcRecord.getBlobUrl());
        let layer = this.getLayerForId(layerId);
        if (!layer || !layer.boundingBox) {
            console.log('error no layer');
            return null;
        }
        let bb = new Rectangle(layer.boundingBox.x, layer.boundingBox.y, layer.boundingBox.width, layer.boundingBox.height);
        let roi = bb.multiply(new Size(srcImage.width, srcImage.height));
        let croppedImage = await repository._mediaImageRepositoryProcessing.cropHtmlImage(srcImage, roi);
        let croppedImageSrc = croppedImage.src;
        await repository.storeImageBlobString(layerId, layerStateId, targetMediaImageType, croppedImageSrc)
        
        let croppedRecord = await repository.getRecord(layerId, layerStateId, targetMediaImageType);
        return croppedRecord;
    }
}